import React, { useState, useEffect } from "react";
import { api } from "../../lib/api";
import { Alert, EmptyState, Field } from "../../components";
import { RATING_LABEL } from "../../lib/ratings";
import ScoreRing from "./ScoreRing";
import "./Reviews.css";

const CHANGE_TONE = { improved: "success", regressed: "danger", new: "accent" };
const CHANGE_LABEL = { improved: "Improved", regressed: "Regressed", new: "Newly assessed", unchanged: "No change" };

// Two reviews side by side. Movement is the only thing on screen, because movement is
// what a quarterly review is for.
const ComparisonPanel = ({ assessment, siblings }) => {
  const [toId, setToId] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!toId) { setData(null); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // The older assessment is always the baseline, whichever way round they were
        // picked: "improved" has to mean improved over time, not over list order.
        const [older, newer] = [assessment, siblings.find((s) => String(s.id) === String(toId))]
          .sort((a, b) => String(a.assessment_date).localeCompare(String(b.assessment_date)));
        const res = await api.get(`s4/api/assessments/${older.id}/compare?to=${newer.id}`);
        if (!cancelled) { setData(res); setError(""); }
      } catch (e) {
        if (!cancelled) { setError(e.message || "Could not compare those assessments."); setData(null); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [toId, assessment, siblings]);

  if (!siblings.length) {
    return (
      <EmptyState title="Nothing to compare against">
        A comparison needs a second assessment for {assessment.organization_name}. Clone
        this one when the next review comes round.
      </EmptyState>
    );
  }

  const delta = data?.point_change;
  const tone = delta === null || delta === undefined || delta === 0 ? "flat" : delta > 0 ? "up" : "down";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: ".875rem" }}>
      <Field label="Compare against">
        <select className="v2-input" style={{ maxWidth: "22rem" }} value={toId}
                onChange={(e) => setToId(e.target.value)}>
          <option value="">Choose another assessment…</option>
          {siblings.map((s) => (
            <option key={s.id} value={s.id}>
              {s.title} — {String(s.assessment_date).slice(0, 10)}
            </option>
          ))}
        </select>
      </Field>

      <Alert>{error}</Alert>
      {loading && <p className="v2-hint">Comparing…</p>}

      {data && (
        <>
          <div className="v2-cmp-head">
            <div style={{ textAlign: "center" }}>
              <ScoreRing score={data.before.score} small />
              <div className="v2-feed-sub" style={{ marginTop: ".25rem" }}>
                {String(data.before.date).slice(0, 10)}
              </div>
            </div>
            <span aria-hidden="true" style={{ color: "var(--v2-text-muted)" }}>→</span>
            <div style={{ textAlign: "center" }}>
              <ScoreRing score={data.after.score} small />
              <div className="v2-feed-sub" style={{ marginTop: ".25rem" }}>
                {String(data.after.date).slice(0, 10)}
              </div>
            </div>
            <div>
              <div className="v2-kpi-label">Point change</div>
              <div className={`v2-delta v2-delta--${tone}`}>
                {delta === null || delta === undefined ? "—"
                  : delta > 0 ? `up ${delta}` : delta < 0 ? `down ${Math.abs(delta)}` : "no change"}
              </div>
            </div>
            <div className="v2-pills">
              <span className="v2-rpill v2-rpill--green">{data.tally.improved} improved</span>
              <span className="v2-rpill v2-rpill--red">{data.tally.regressed} regressed</span>
              <span className="v2-rpill v2-rpill--unrated">{data.tally.new} newly assessed</span>
            </div>
          </div>

          <div className="v2-tablewrap">
            <table className="v2-table">
              <thead>
                <tr>
                  <th>Assessment item</th>
                  <th>{String(data.before.date).slice(0, 10)}</th>
                  <th>{String(data.after.date).slice(0, 10)}</th>
                  <th>Change</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.name}>
                    <td>{r.name}</td>
                    <td className="v2-dim">{r.before ? RATING_LABEL[r.before] : "Not present"}</td>
                    <td className="v2-dim">{RATING_LABEL[r.after]}</td>
                    <td>
                      {r.change === "unchanged" ? <span className="v2-dim">No change</span> : (
                        <span className={`v2-rbadge v2-rbadge--${CHANGE_TONE[r.change]}`}>
                          {CHANGE_LABEL[r.change].toUpperCase()}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default ComparisonPanel;
